import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { type Map, type MapMouseEvent } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Plus, Check, X, Locate, ZoomIn } from "lucide-react";
import type { Spot } from "@/lib/types";
import type { LegalityResult, LegalityState } from "@/lib/types";
import type { BBox } from "@/hooks/useSpots";
import { LegalityBadge } from "./LegalityBadge";
import { t } from "@/lib/i18n";

/**
 * Full-screen MapLibre map (spec 06 §2 screen 1, §3, §5).
 *
 * - Basemap: a placeholder empty/light style (dark background only) because real
 *   PMTiles from R2 are not available yet. When VITE_TILES_URL is set, a PMTiles
 *   source is wired up; otherwise we render zones/spots over a plain backdrop.
 * - Spot markers: clustered GeoJSON source; tapping a cluster zooms in, tapping
 *   a spot opens the detail sheet.
 * - Zone overlay: PMTiles vector source styled by category (red forbidden,
 *     amber time-dependent / pedestrian). Rendered when tiles are configured.
 * - Spot data is fetched on map idle (debounced in the parent's useSpots hook).
 *
 * Markers are procedural SVG (AGENTS.md constitution §3 — no committed images).
 */
/** Phase of the create-spot flow owned by the parent state machine. */
export type CreateMode = "idle" | "positioning" | "details";

export interface MapViewProps {
  spots: Spot[];
  /** GeoJSON zone overlay (100m-buffered facilities + pedestrian zones). */
  zones: GeoJSON.FeatureCollection | null;
  /** Called when the map becomes idle with a new viewport bbox. */
  onBoundsChange: (b: BBox) => void;
  /** Called when a spot marker is tapped. */
  onSpotSelect: (spot: Spot) => void;
  /**
   * Enter "create spot" positioning phase. Receives the current map center so
   * the pin spawns in the middle of the viewport.
   */
  onCreateSpot: (lng: number, lat: number) => void;
  /** Fly to the user's geolocation. */
  userLocation: { lng: number; lat: number } | null;
  /** Current create-flow phase (controls the FAB morph + create marker). */
  createMode?: CreateMode;
  /** Live pin coordinates while positioning/details. */
  pinCoord?: { lng: number; lat: number } | null;
  /** Called as the user drags the pin (debounced legality runs in the parent). */
  onPinMove?: (lng: number, lat: number) => void;
  /** Confirm the pin position → parent switches to the details phase. */
  onConfirmPin?: () => void;
  /** Abort the create flow. */
  onCancelCreate?: () => void;
  /** Live legality state for the pin (enables/disables the confirm FAB). */
  legalityState?: LegalityState;
  legalityResult?: LegalityResult | null;
  legalityLoading?: boolean;
  /** Fly to a target position (e.g. from address search). Clears on arrival. */
  flyToTarget?: { lng: number; lat: number } | null;
  /** Called after a flyTo completes (to clear the target). */
  onFlyComplete?: () => void;
}

/** Default center: Berlin Mitte. */
const DEFAULT_CENTER: [number, number] = [13.405, 52.52];
const DEFAULT_ZOOM = 12;

/**
 * Pin element for the create-flow marker. Uses the custom Stnr.Space cannabis
 * coin pin image (pin.png). For the draggable variant, adds a grab cursor.
 */
function createPinElement(draggable: boolean): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText = draggable
    ? "width:40px;height:40px;cursor:grab;filter:drop-shadow(0 4px 6px rgba(0,0,0,.5));"
    : "width:40px;height:40px;filter:drop-shadow(0 4px 6px rgba(0,0,0,.5));";
  const img = document.createElement("img");
  img.src = "/pin.webp";
  img.alt = "";
  img.style.cssText = "width:100%;height:100%;object-fit:contain;";
  el.appendChild(img);
  return el;
}

export function MapView({
  spots,
  zones,
  onBoundsChange,
  onSpotSelect,
  onCreateSpot,
  userLocation,
  createMode = "idle",
  pinCoord,
  onPinMove,
  onConfirmPin,
  onCancelCreate,
  legalityState = "grey",
  legalityResult,
  legalityLoading,
  flyToTarget,
  onFlyComplete,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const createMarkerRef = useRef<maplibregl.Marker | null>(null);
  // Mirrors the current create phase so the one-time init effect's click
  // handler can suppress spot taps while positioning without re-binding.
  const createModeRef = useRef<CreateMode>(createMode);
  createModeRef.current = createMode;
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);

  // --- One-time map init ---
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const maptilerKey = import.meta.env.VITE_MAPTILER_KEY;

    // MapTiler provides a complete style JSON (includes sources, layers, glyphs, sprite).
    // Dark theme: "dataviz-dark". Other options: "streets-v4", "toner", "topo-v2".
    const style = maptilerKey
      ? `https://api.maptiler.com/maps/dataviz-dark/style.json?key=${maptilerKey}`
      : {
          version: 8 as const,
          sources: {},
          layers: [
            {
              id: "background",
              type: "background" as const,
              paint: { "background-color": "#0a0f0a" },
            },
          ],
        };

    const map = new maplibregl.Map({
      container: containerRef.current,
      style,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    });

    mapRef.current = map;

    map.on("load", () => {
      // --- Spot source (clustered GeoJSON) ---
      map.addSource("spots", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 16,
      });

      // Cluster circles.
      map.addLayer({
        id: "spots-cluster",
        type: "circle",
        source: "spots",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#38d17a",
          "circle-radius": [
            "step",
            ["get", "point_count"],
            16,
            10,
            22,
            50,
            28,
          ],
          "circle-stroke-color": "#06140b",
          "circle-stroke-width": 2,
          "circle-opacity": 0.9,
        },
      });

      // Cluster count labels.
      map.addLayer({
        id: "spots-cluster-label",
        type: "symbol",
        source: "spots",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 13,
          "text-font": ["Noto Sans Regular"],
        },
        paint: {
          "text-color": "#06140b",
        },
      });

      // Unclustered spot markers — circle + text label (no async icon loading).
      // Simpler and more reliable than symbol layers with loadImage.
      map.addLayer({
        id: "spots-point",
        type: "circle",
        source: "spots",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["get", "kind"],
            "indoor_club",
            "#2bb868",
            "#38d17a",
          ],
          "circle-radius": 8,
          "circle-stroke-color": "#06140b",
          "circle-stroke-width": 2,
          "circle-opacity": 0.95,
        },
      });
      // Spot name labels (only at higher zoom to avoid clutter).
      map.addLayer({
        id: "spots-point-label",
        type: "symbol",
        source: "spots",
        filter: ["!", ["has", "point_count"]],
        minzoom: 14,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": 11,
          "text-offset": [0, -1.8],
          "text-anchor": "bottom",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#e8f0e8",
          "text-halo-color": "#06140b",
          "text-halo-width": 2,
        },
      });

      // --- Zone overlay (GeoJSON from PostGIS, dynamic per viewport) ---
      map.addSource("zones", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // Forbidden facility zones (100m buffers): red fill + outline.
      map.addLayer({
        id: "zones-forbidden-fill",
        type: "fill",
        source: "zones",
        minzoom: 12,
        filter: ["==", ["get", "kind"], "forbidden"],
        paint: {
          "fill-color": "#e0564f",
          "fill-opacity": 0.25,
        },
      });
      map.addLayer({
        id: "zones-forbidden-line",
        type: "line",
        source: "zones",
        minzoom: 12,
        filter: ["==", ["get", "kind"], "forbidden"],
        paint: {
          "line-color": "rgba(224,86,79,0.7)",
          "line-width": 1,
        },
      });

      // Pedestrian zones: amber fill.
      map.addLayer({
        id: "zones-pedestrian-fill",
        type: "fill",
        source: "zones",
        minzoom: 12,
        filter: ["==", ["get", "kind"], "pedestrian"],
        paint: {
          "fill-color": "#f0a93b",
          "fill-opacity": 0.3,
        },
      });

      emitBounds();
      setZoomLevel(Math.round(map.getZoom()));
    });

    map.on("zoom", () => setZoomLevel(Math.round(map.getZoom())));

    // Spot tap → open detail; cluster tap → zoom in.
    // Spot tap → open detail; cluster tap → zoom in. Suppressed while the
    // user is positioning a new pin (no accidental detail opens).
    const handleClick = (e: MapMouseEvent & { features?: unknown }) => {
      if (createModeRef.current !== "idle") return;
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["spots-point", "spots-cluster"],
      });
      const f = features[0];
      if (!f) return;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.cluster || props.point_count != null) {
        // Cluster: zoom in.
        const geom = f.geometry as unknown as { coordinates: [number, number] };
        map.flyTo({ center: geom.coordinates, zoom: map.getZoom() + 2 });
        return;
      }
      // Unclustered spot: surface to parent.
      const id = String(props.id);
      const lng = (props.lng as number) ?? 0;
      const lat = (props.lat as number) ?? 0;
      onSpotSelect({
        id,
        kind: (props.kind as Spot["kind"]) ?? "outdoor",
        name: (props.name as string) ?? "",
        description: (props.description as string) ?? "",
        location: { lng, lat },
        legality_at_creation: {} as Spot["legality_at_creation"],
        created_by: (props.created_by as string) ?? "",
        created_at: (props.created_at as string) ?? "",
        status: "confirmed",
        rating_count: (props.rating_count as number) ?? 0,
        rating_avg: (props.rating_avg as number) ?? 0,
      });
    };
    map.on("click", "spots-point", handleClick);
    map.on("click", "spots-cluster", handleClick);
    map.on("mouseenter", "spots-point", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "spots-point", () => {
      map.getCanvas().style.cursor = "";
    });

    // Emit bounds on idle → parent fetches spots (debounced in useSpots).
    const emitBounds = () => {
      const b = map.getBounds();
      onBoundsChange({
        minLng: b.getWest(),
        minLat: b.getSouth(),
        maxLng: b.getEast(),
        maxLat: b.getNorth(),
      });
    };
    map.on("moveend", emitBounds);
    map.on("zoomend", emitBounds);

    // Cleanup
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Update spot source when spots change ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource("spots")) return;

    const fc = {
      type: "FeatureCollection" as const,
      features: spots.map((s) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [s.location.lng, s.location.lat] as [number, number],
        },
        properties: {
          id: s.id,
          kind: s.kind,
          name: s.name,
          description: s.description,
          created_by: s.created_by,
          created_at: s.created_at,
          rating_count: s.rating_count,
          rating_avg: s.rating_avg,
          lng: s.location.lng,
          lat: s.location.lat,
        },
      })),
    };
    (map.getSource("spots") as maplibregl.GeoJSONSource).setData(fc);
  }, [spots]);

  // --- Update zone overlay when zones change ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource("zones")) return;
    const source = map.getSource("zones") as maplibregl.GeoJSONSource;
    if (zones) {
      source.setData(zones);
    } else {
      source.setData({ type: "FeatureCollection", features: [] });
    }
  }, [zones]);

  // --- Fly to user location ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userLocation) return;
    map.flyTo({
      center: [userLocation.lng, userLocation.lat],
      zoom: Math.max(map.getZoom(), 14),
    });
  }, [userLocation]);

  // --- Fly to target (from address search) ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyToTarget) return;
    map.flyTo({
      center: [flyToTarget.lng, flyToTarget.lat],
      zoom: Math.max(map.getZoom(), 15),
    });
    map.once("moveend", () => onFlyComplete?.());
  }, [flyToTarget, onFlyComplete]);

  // --- User location marker (only when real geolocation is available) ---
  const userMarkerEl = useMemo(
    () =>
      typeof document !== "undefined"
        ? (() => {
            const el = document.createElement("div");
            el.style.cssText =
              "width:16px;height:16px;border-radius:50%;background:#38d17a;border:3px solid #06140b;box-shadow:0 0 0 4px rgba(56,209,122,.3);";
            return el;
          })()
        : null,
    [],
  );
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userMarkerEl || !userLocation) return;
    const marker = new maplibregl.Marker({ element: userMarkerEl });
    marker.setLngLat([userLocation.lng, userLocation.lat]).addTo(map);
    return () => {
      marker.remove();
    };
  }, [userMarkerEl, userLocation]);

  // --- Create-flow pin: draggable while positioning, static during details ---
  // Depends on createMode only (NOT pinCoord values) so dragging the marker
  // never recreates it mid-drag. The latest coordinates are read from a ref.
  const pinCoordRef = useRef(pinCoord);
  pinCoordRef.current = pinCoord;
  useEffect(() => {
    const map = mapRef.current;
    // Idle (or no coords yet): tear down any existing create marker.
    if (!map || createMode === "idle" || !pinCoordRef.current) {
      if (createMarkerRef.current) {
        createMarkerRef.current.remove();
        createMarkerRef.current = null;
      }
      return;
    }

    const draggable = createMode === "positioning";
    const start = pinCoordRef.current;
    const marker = new maplibregl.Marker({
      element: createPinElement(draggable),
      draggable,
      anchor: "bottom",
    })
      .setLngLat([start.lng, start.lat])
      .addTo(map);

    if (draggable) {
      marker.on("drag", () => {
        const ll = marker.getLngLat();
        onPinMove?.(ll.lng, ll.lat);
      });
    }

    createMarkerRef.current = marker;
    return () => {
      marker.remove();
      createMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createMode]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      {/* Create-flow FAB: morphs + → ✓ and gains a cancel (✗) neighbor. */}
      {createMode === "positioning" ? (
        <div className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+1rem)] z-10 flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => onConfirmPin?.()}
            disabled={legalityLoading}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-contrast shadow-lg ring-2 ring-white/40 transition active:scale-95 disabled:opacity-50"
            aria-label={t("create.confirm")}
          >
            <Check size={24} />
          </button>
          <button
            type="button"
            onClick={() => onCancelCreate?.()}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-surface text-fg shadow-lg ring-1 ring-line transition active:scale-95"
            aria-label={t("create.cancel")}
          >
            <X size={18} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            const map = mapRef.current;
            if (!map) return;
            const c = map.getCenter();
            onCreateSpot(c.lng, c.lat);
          }}
          className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+1rem)] z-10 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-contrast shadow-lg transition active:scale-95"
          aria-label={t("nav.createSpot")}
        >
          <Plus size={24} />
        </button>
      )}

      {/* Live legality pill while positioning the pin. */}
      {createMode === "positioning" ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-10 flex justify-center px-4">
          <LegalityBadge
            state={legalityState}
            result={legalityResult}
            loading={legalityLoading}
            variant="pill"
          />
        </div>
      ) : null}

      {/* Floating action: locate me (hidden while positioning a new pin) */}
      {createMode !== "positioning" ? (
        <button
          type="button"
          onClick={() => {
            navigator.geolocation?.getCurrentPosition((pos) => {
              const map = mapRef.current;
              if (!map) return;
              map.flyTo({
                center: [pos.coords.longitude, pos.coords.latitude],
                zoom: Math.max(map.getZoom(), 14),
              });
            });
          }}
          className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+4.5rem)] z-10 flex h-12 w-12 items-center justify-center rounded-full bg-surface text-fg shadow-lg ring-1 ring-line transition active:scale-95"
          aria-label={t("nav.locateMe")}
        >
          <Locate size={20} />
        </button>
      ) : null}

      {/* Zoom hint: zones only render at zoom >= 12 */}
      {zoomLevel < 12 && createMode === "idle" ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-20 z-10 flex justify-center px-4">
          <div className="flex items-center gap-2 rounded-full bg-bg-elevated/95 px-4 py-2 text-xs font-medium text-fg-muted shadow-lg ring-1 ring-line">
            <ZoomIn size={14} className="text-accent" />
            {t("map.zoomHint")}
          </div>
        </div>
      ) : null}

      {/* Zoom level indicator */}
      <div className="absolute bottom-8 left-2 z-10 rounded-md bg-black/70 px-2 py-0.5 text-xs font-mono text-white/80 pointer-events-none">
        Z{zoomLevel}
      </div>

      {/* Persistent legal disclaimer footer */}
      <div className="pointer-events-none absolute bottom-0 left-0 z-10 bg-gradient-to-t from-black/80 to-transparent pb-[env(safe-area-inset-bottom,0px)] pt-6">
        <p className="max-w-md px-3 text-left text-[11px] leading-tight text-white/45">
          {t("legal.footer")}
        </p>
      </div>
    </div>
  );
}
